<script setup lang="ts">
import {
    checkStripeAccountReady,
    currentSupportStripeStatus
} from '@partner-spa-vue/stores/CurrentWebsiteStripeStatusStore';
import { pageTitle } from '@partner-spa-vue/stores/LayoutStore';
import { toast } from '@/ui-plus/sonner';
import { currentWebsite, currentWebsiteId } from '@partner-spa-vue/stores/MyWebsitesStore';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import Badge from '@/components/ui/badge/Badge.vue';
import { zmgAdminOnlyCacheUpdateClient } from '@api-client/zmg_cache';
import { ChevronRight } from 'lucide-vue-next';

pageTitle.value = 'Support Partnership';

const supportPartnerLinks = [
    // { name: 'support-earnings-stripe', label: 'Business and bank details' },
    { name: 'support-earnings-stripe', label: 'Stripe' },
    { name: 'support-earnings-fee-settings', label: 'Fee Settings' },
    { name: 'support-earnings-balances', label: 'Balances' },
    { name: 'support-earnings-incoming', label: 'Incoming' },
    { name: 'support-earnings-unpaid', label: 'Unpaid' },
    { name: 'support-earnings-history', label: 'History' },
    { name: 'support-earnings-reports', label: 'Reports' },
    { name: 'support-earnings-notifications', label: 'Notifications' },
    { name: 'support-earnings-imprint', label: 'Imprint' },
] as const;

const onPartnerChange = async (checked: boolean) => {
    if (currentWebsite.value) {
        currentWebsite.value.isSupportTeamPartner = checked;
    }
    await saveChanges();
}

const saveChanges = async () => {
    const { error } = await (await zmgAdminOnlyCacheUpdateClient.website.partner_info[':id'].$patch({
        param: { id: currentWebsiteId.value ?? '' },
        json: {
            isSupportTeamPartner: currentWebsite.value?.isSupportTeamPartner ?? false,
        }
    })).json();
    if (error) {
        console.error(error);
        toast.error('Failed to update partner');
    } else {
        toast.success('Partner settings updated');
    }
}
</script>

<template>
    <div class="max-w-3xl mx-auto">
        <Card>
            <CardHeader>
                <CardTitle>
                    Support Partnership
                </CardTitle>
                <CardDescription>
                    Help stores and delivery teams get started with their own ordering websites.
                    add created website to google maps and social media, and earn a fee from orders.
                    When you support a store once it receives orders through any website on this platform, you
                    can earn a fee from those orders as a Support Partner. (check 'Fee settings' below to set)
                    <br>
                    <br>
                    Use the sidebar to manage your settings, balances, earnings history and more.
                </CardDescription>
            </CardHeader>
            <CardContent>
                <label for="partner"
                       class="flex items-center justify-between p-4 border rounded-lg cursor-pointer">
                    <div class="space-y-0.5">
                        <h3 class="text-lg font-medium">Partner</h3>
                        <p class="text-muted-foreground">
                            Become a partner to support stores and earn a fee of their orders.
                            <br>
                            <br>
                            <Badge v-if="currentWebsite?.defaultHostname"
                                   class="ml-2">
                                {{ currentWebsite?.defaultHostname }}
                            </Badge>
                            You will automatically become the support partner for any stores created on your
                            website, and all stores in other websites you already support.
                        </p>
                    </div>
                    <div class="flex items-center space-x-3">
                        <Switch id="partner"
                                :checked="currentWebsite?.isSupportTeamPartner ?? false"
                                @update:checked="onPartnerChange"
                                class="scale-125">
                        </Switch>
                        <span class="font-medium">{{ currentWebsite?.isSupportTeamPartner ? 'Yes' : 'No' }}</span>
                    </div>
                </label>
                <template v-if="currentWebsite?.isSupportTeamPartner">
                    <div class="border-t my-6"></div>
                    <nav class="border rounded-lg divide-y">
                        <RouterLink v-for="link in supportPartnerLinks"
                                    :key="link.name"
                                    :to="{ name: link.name }"
                                    class="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors">
                            <span class="font-medium">
                                {{ link.label }}
                                <span v-if="link.name === 'support-earnings-stripe'">
                                    {{ checkStripeAccountReady(currentSupportStripeStatus) === false ? '⚠️' : '' }}
                                </span>
                            </span>
                            <ChevronRight class="h-4 w-4 text-muted-foreground" />
                        </RouterLink>
                    </nav>
                </template>
            </CardContent>
        </Card>
    </div>
</template>
